import { z } from "zod";
import { ORGANIZATION_MEMBER_ROLES, ORGANIZATION_STATUSES } from "./constants.js";
import { apiBigIntIdSchema, apiIntIdSchema } from "./ids.js";
import { organizationUsageResponseSchema } from "./organization-usage-contracts.js";
import { cursorQuerySchema } from "./pagination.js";
/**
 * Super Admin is PLATFORM-level authorization, independent of any
 * OrganizationMember role. These contracts back the isolated `/super-admin/*`
 * namespace only — never the tenant `/organizations/:organizationId/*` routes.
 */
export const superAdminSelfResponseSchema = z.object({
    userId: apiIntIdSchema,
    email: z.string().email(),
    displayName: z.string(),
    isProtected: z.boolean(),
});
const superAdminListQuerySchema = cursorQuerySchema.extend({
    search: z.string().trim().min(1).max(200).optional(),
});
export const superAdminOrganizationListQuerySchema = superAdminListQuerySchema;
/**
 * Counts of non-deleted organizations. Soft-deleted rows are not customer
 * organizations and are excluded. V1 statuses are ACTIVE and SUSPENDED.
 */
export const superAdminDashboardSummarySchema = z.object({
    total: z.number().int().nonnegative(),
    active: z.number().int().nonnegative(),
    suspended: z.number().int().nonnegative(),
});
/**
 * Entitlement flags for display. These are not provider connection health.
 */
export const organizationPlatformEntitlementsSchema = z.object({
    whatsappEnabled: z.boolean(),
    facebookEnabled: z.boolean(),
    instagramEnabled: z.boolean(),
});
/**
 * Super Admin organization list row. Includes SUSPENDED organizations.
 * Soft-deleted organizations are excluded. Usage matches Order 35.
 */
export const superAdminOrganizationSummarySchema = z.object({
    id: apiIntIdSchema,
    name: z.string(),
    status: z.enum(ORGANIZATION_STATUSES),
    createdAt: z.string(),
    usage: organizationUsageResponseSchema,
    platformEntitlements: organizationPlatformEntitlementsSchema,
    isBetaOrganization: z.boolean(),
});
export const superAdminOrganizationListResponseSchema = z.object({
    items: z.array(superAdminOrganizationSummarySchema),
    nextCursor: z.string().nullable(),
});
/**
 * `GET /super-admin/organization-directory`: lightweight list for the
 * organization switcher. Selecting a row is not authority; every
 * organization request is still authorized by the server.
 */
export const superAdminOrganizationDirectoryQuerySchema = superAdminListQuerySchema;
export const superAdminOrganizationDirectoryItemSchema = z.object({
    id: apiIntIdSchema,
    name: z.string(),
    status: z.enum(ORGANIZATION_STATUSES),
});
export const superAdminOrganizationDirectoryResponseSchema = z.object({
    items: z.array(superAdminOrganizationDirectoryItemSchema),
    nextCursor: z.string().nullable(),
});
export const superAdminUserListQuerySchema = superAdminListQuerySchema;
export const superAdminUserSummarySchema = z.object({
    id: apiIntIdSchema,
    email: z.string().email(),
    displayName: z.string(),
    status: z.enum(["ACTIVE", "DISABLED"]),
    createdAt: z.string(),
    /** Count of active (non-deleted, ACTIVE) OrganizationMember rows for this User. */
    organizationCount: z.number().int().nonnegative(),
});
export const superAdminUserListResponseSchema = z.object({
    items: z.array(superAdminUserSummarySchema),
    nextCursor: z.string().nullable(),
});
export const superAdminOrganizationDetailSchema = z.object({
    id: apiIntIdSchema,
    name: z.string(),
    status: z.enum(ORGANIZATION_STATUSES),
    timezone: z.string(),
    createdAt: z.string(),
    usage: organizationUsageResponseSchema,
    platformEntitlements: organizationPlatformEntitlementsSchema,
    isBetaOrganization: z.boolean(),
});
/**
 * Organization status is configuration only. ACTIVE and SUSPENDED are the only
 * values. There is no expiration and no CLOSED status. Runtime effects of
 * suspension are a later order.
 */
export const updateSuperAdminOrganizationStatusRequestSchema = z
    .object({
    status: z.enum(ORGANIZATION_STATUSES),
})
    .strict();
export { updateSuperAdminOrganizationBetaRequestSchema, updateSuperAdminBetaFeatureRequestSchema, superAdminBetaFeatureListResponseSchema, superAdminBetaFeatureItemSchema, } from "./beta-features-contracts.js";
/** One non-deleted OrganizationMember row of a User, read-only for Super Admin. */
export const superAdminUserMembershipSchema = z.object({
    organizationMemberId: apiIntIdSchema,
    organizationId: apiIntIdSchema,
    organizationName: z.string(),
    organizationStatus: z.enum(ORGANIZATION_STATUSES),
    role: z.enum(ORGANIZATION_MEMBER_ROLES),
    memberStatus: z.enum(["ACTIVE", "SUSPENDED"]),
});
export const superAdminUserDetailSchema = z.object({
    id: apiIntIdSchema,
    email: z.string().email(),
    displayName: z.string(),
    status: z.enum(["ACTIVE", "DISABLED"]),
    createdAt: z.string(),
    memberships: z.array(superAdminUserMembershipSchema),
    /** Read-only. Platform Admin changes go through Super Admin Team. */
    platformAdmin: z
        .object({
        id: apiIntIdSchema,
        isProtected: z.boolean(),
    })
        .nullable(),
});
/** Unrevoked, unexpired Session. Token material is never part of this shape. */
export const superAdminUserSessionSchema = z.object({
    id: apiBigIntIdSchema,
    createdAt: z.string(),
    lastUsedAt: z.string().nullable(),
    expiresAt: z.string(),
    ipAddress: z.string().nullable(),
    userAgent: z.string().nullable(),
    /** The Session making this request. */
    current: z.boolean(),
});
export const superAdminUserSessionListResponseSchema = z.object({
    items: z.array(superAdminUserSessionSchema),
});
export const superAdminRevokeSessionsResponseSchema = z.object({
    revokedCount: z.number().int().nonnegative(),
});
export const superAdminPlatformAdminSchema = z.object({
    id: apiIntIdSchema,
    userId: apiIntIdSchema,
    email: z.string().email(),
    displayName: z.string(),
    isProtected: z.boolean(),
    createdAt: z.string(),
});
export const superAdminPlatformAdminListResponseSchema = z.object({
    items: z.array(superAdminPlatformAdminSchema),
});
/** Grants Platform Admin to an existing User. Never creates a User. */
export const addSuperAdminPlatformAdminRequestSchema = z
    .object({
    email: z.string().trim().toLowerCase().email().max(254),
})
    .strict();
//# sourceMappingURL=super-admin-contracts.js.map